import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  SpaceDashboard,
  PeopleAlt,
  Token,
  Schema,
  Cable,
  Sync,
  Folder,
  Chat as ChatIcon,
  ExpandMore,
  ChevronRight,
  Description,
  Hub,
  Settings,
  DataObject,
  Transform,
  AccountTree,
  Person,
  Build,
  Assessment,
  BarChart,
  CompareArrows,
  Api,
  Queue,
  History,
  Webhook,
  Tune,
  HelpOutline,
  Storage,
  Notifications,
  LibraryBooks,
  Groups,
  IntegrationInstructions,
  SwapHoriz,
  MenuBook,
  Psychology,
  Sensors,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ensureMasterMenus, fetchSidebarMenus } from '../../services/rolesMenuApi';
import { useUserRights } from '../../context/UserRightsContext';

const CLOSED_DRAWER_WIDTH = 64;
const OPEN_DRAWER_WIDTH = 300;
const HEADER_HEIGHT = 56;
const CURRENT_USER_ID_URL = `${(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002/').replace(/\/$/, '')}/synctrack/current-user-id`;

const ICON_BY_CODE = {
  HUB: Hub,
  SYNC_FILE: Folder,
  AI_AGENTS: Settings,
  CONFIGURATION: Tune,
  CDX: Cable,
  HELP: HelpOutline,
  HUB_SYNC_FLOW: Sync,
  HUB_HUB_FLOW: Hub,
  HUB_TRANSFORMATION: Transform,
  HUB_DATA_SCHEMA: Schema,
  HUB_DATA_POINT: AccountTree,
  IOT: Sensors,
  IOT_DATA_VIEW: Sensors,
  SF_DESIGN_SHARE: Folder,
  SF_KNOWLEDGE_HUB: LibraryBooks,
  SF_FILE_EXCHANGE: SwapHoriz,
  SF_BENCHMARK_HUB: Assessment,
  SF_BENCHMARK_PROJECTS: Folder,
  SF_CODE_STANDARD: Build,
  SF_BENCHMARK: BarChart,
  SF_BENCHMARK_COMPARISON: CompareArrows,
  AI_AGENT_SETUP: Settings,
  AI_AGENT_TOOLS: Build,
  AI_MODEL_CONFIG: Tune,
  AI_TASK_QUEUE: Queue,
  AI_CHAT_HUB: ChatIcon,
  AI_AGENT_TEAMS: Groups,
  AI_PDF_HUB: Description,
  CFG_DB_CONFIG: Storage,
  CFG_CONNECTOR: Token,
  CFG_ROLES: Person,
  CFG_USERS: PeopleAlt,
  CFG_INTEGRATION: IntegrationInstructions,
  CFG_WEBHOOK: Webhook,
  CFG_APP_CONFIG: Settings,
  CFG_NOTIFICATIONS: Notifications,
  CFG_AUDIT_LOGS: History,
  CDX_DASHBOARD: SpaceDashboard,
  CDX_TENANT: PeopleAlt,
  CDX_TEST_API: Api,
  CDX_MASTER_CONNECTOR: Cable,
  CDX_MASTER_FLOWS: Schema,
  CDX_MASTER_LLM: Psychology,
  CDX_GLOBAL_TRANSFORM: Transform,
  CDX_GLOBAL_DATA_SCHEMA: Schema,
  CDX_GLOBAL_TOOLS: Build,
  CDX_GLOBAL_ROLES: Person,
  CDX_GLOBAL_USERS: PeopleAlt,
  CDX_GLOBAL_AI_AGENTS: Settings,
  CDX_GLOBAL_PROMPT_LIB: LibraryBooks,
  CDX_MASTER_MENUS: AccountTree,
  CDX_LICENSE: Assessment,
  CDX_AI_GLOBAL_SCHEMA: DataObject,
  CDX_FILE_SHARE_USAGE: Assessment,
  CDX_WEBHOOK_EVENT_REG: Webhook,
  CDX_GLOBAL_LINK_REL: CompareArrows,
  CDX_APP_CONFIG: Settings,
  HELP_HELP: HelpOutline,
};

const resolveIcon = (menu) => ICON_BY_CODE[menu.menuCode] || MenuBook;

/**
 * Intended product sidebar (doc §6):
 * Menus from master_menu via effective API — not hardcoded.
 * CDX → full catalog. Tenant → license ∩ tenant menus ∩ role Access (server-side).
 * Structure order: Hub | Sync File | AI Agents | Configuration | CDX | Help
 */
const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { canAccessMenu, rightsLoaded } = useUserRights();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isCdxUser = useMemo(() => {
    try {
      const v = localStorage.getItem('cdx_user');
      return v === 'true' || v === true;
    } catch {
      return false;
    }
  }, []);

  const tenantId = useMemo(() => {
    try {
      return localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');
    } catch {
      return null;
    }
  }, []);

  const resolveUserId = useCallback(async () => {
    try {
      const stored = localStorage.getItem('userId') || localStorage.getItem('user_id');
      if (stored) return stored;
      const token = localStorage.getItem('token');
      if (!token) return null;
      const res = await fetch(CURRENT_USER_ID_URL, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const id = data?.userId ?? data?.user_id ?? null;
      if (id != null) localStorage.setItem('userId', String(id));
      return id;
    } catch {
      return null;
    }
  }, []);

  const buildSections = useCallback((menus) => {
    if (!Array.isArray(menus) || menus.length === 0) return [];
    const roots = menus
      .filter((m) => m.menuLevel === 1 || !m.parentMenuId)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const byParent = new Map();
    menus
      .filter((m) => m.parentMenuId)
      .forEach((m) => {
        const key = String(m.parentMenuId);
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(m);
      });

    return roots
      .map((root) => {
        const children = (byParent.get(String(root.menuId)) || []).sort(
          (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        );
        const items = children
          .map((c) => {
            const path = c.menuPath || null;
            const code = String(c.menuCode || '').toUpperCase();
            // License is Tenants → Subscriptions only — hide from sidebar
            if (code === 'CDX_LICENSE' || path === '/admin/license') {
              return null;
            }
            const enabled = Boolean(path);
            // CDX: full catalog (pathless stay visible but grey). Tenant: Access by menu_code or name.
            const visible = isCdxUser
              ? true
              : enabled && (canAccessMenu(c.menuCode) || canAccessMenu(c.menuName));
            return {
              text: c.menuName,
              path,
              icon: resolveIcon(c),
              enabled,
              menuCode: c.menuCode,
              visible,
            };
          })
          .filter((item) => item && item.visible);

        return {
          id: root.menuCode || String(root.menuId),
          title: root.menuName,
          icon: resolveIcon(root),
          items,
        };
      })
      .filter((sec) => sec.items.length > 0);
  }, [isCdxUser, canAccessMenu]);

  const load = useCallback(async () => {
    if (!tenantId) {
      setSections([]);
      setError('No tenant context');
      setLoading(false);
      return;
    }
    // Wait for rights so client-side Access filter is ready (non-CDX only)
    if (!isCdxUser && !rightsLoaded) {
      setLoading(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await ensureMasterMenus().catch(() => null);
      const userId = isCdxUser ? null : await resolveUserId();
      if (!isCdxUser && (userId == null || userId === '')) {
        setSections([]);
        setError('User id missing — cannot resolve role menus');
        return;
      }
      const rows = await fetchSidebarMenus(tenantId, {
        includePlatformOnly: isCdxUser,
        cdxUser: isCdxUser,
        userId,
      });
      setSections(buildSections(rows));
    } catch (err) {
      console.error('Effective sidebar load failed:', err);
      setSections([]);
      setError(err.message || 'Failed to load menus');
    } finally {
      setLoading(false);
    }
  }, [tenantId, isCdxUser, rightsLoaded, resolveUserId, buildSections]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const currentPath = location.pathname;
    const sectionToExpand = sections.find((section) =>
      section.items.some(
        (item) =>
          item.path &&
          (currentPath === item.path || currentPath.startsWith(`${item.path}/`))
      )
    );
    if (sectionToExpand && !expandedSections[sectionToExpand.id]) {
      setExpandedSections((prev) => ({ ...prev, [sectionToExpand.id]: true }));
    }
  }, [location.pathname, sections]);

  const handleNavigation = (path) => {
    if (path) navigate(path);
  };

  const handleSectionToggle = (sectionId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const hoverStyle = {
    '&:hover': { backgroundColor: 'rgba(79, 99, 103, 0.08)' },
    '&.Mui-selected': {
      backgroundColor: 'rgba(79, 99, 103, 0.16)',
      '&:hover': { backgroundColor: 'rgba(79, 99, 103, 0.24)' },
    },
  };

  const buttonStyle = {
    minHeight: 40,
    justifyContent: isExpanded ? 'initial' : 'center',
    px: 2,
    borderRadius: isExpanded ? '0 8px 8px 0' : '8px',
    mx: isExpanded ? 0 : 1,
    my: 0.5,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    ...hoverStyle,
  };

  const sectionHeaderStyle = {
    ...buttonStyle,
    fontWeight: 600,
    '&:hover': { backgroundColor: 'rgba(79, 99, 103, 0.08)' },
  };

  const subItemStyle = { ...buttonStyle, pl: isExpanded ? 5 : 2 };
  const disabledStyle = {
    opacity: 0.4,
    cursor: 'not-allowed',
    '&:hover': { backgroundColor: 'transparent' },
  };

  const renderMenuItem = (item) => {
    const IconComponent = item.icon;
    const isSelected =
      item.path &&
      (location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
    const isDisabled = !item.enabled;
    return (
      <ListItem key={item.menuCode || item.text} disablePadding sx={{ width: '100%' }}>
        <ListItemButton
          selected={Boolean(isSelected && !isDisabled)}
          onClick={() => !isDisabled && handleNavigation(item.path)}
          disabled={isDisabled}
          sx={{ ...subItemStyle, ...(isDisabled ? disabledStyle : {}) }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 1.5 : 'auto', justifyContent: 'center' }}>
            <IconComponent
              sx={{
                fontSize: 20,
                color: isDisabled ? 'rgba(0, 0, 0, 0.26)' : theme.palette.primary.main,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isSelected && !isDisabled ? '600' : '500',
              color: isDisabled ? 'rgba(0, 0, 0, 0.26)' : '#3A3A3A',
              noWrap: true,
            }}
            sx={{
              opacity: isExpanded ? 1 : 0,
              display: isExpanded ? 'block' : 'none',
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
      sx={{
        width: isExpanded ? OPEN_DRAWER_WIDTH : CLOSED_DRAWER_WIDTH,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
        bgcolor: 'background.paper',
        borderRight: '1px solid',
        borderColor: 'divider',
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        position: 'sticky',
        top: HEADER_HEIGHT,
        zIndex: theme.zIndex.drawer,
      }}
    >
      <List sx={{ py: 1, width: '100%' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}
        {!loading && error && isExpanded && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {!loading && !error && sections.length === 0 && isExpanded && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              No menus
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isCdxUser
                ? 'Master menu catalog is empty'
                : 'No licensed/assigned menus or role Access'}
            </Typography>
          </Box>
        )}
        {sections.map((section) => {
          const SectionIcon = section.icon;
          const open = !!expandedSections[section.id];
          return (
            <Box key={section.id}>
              <ListItem disablePadding sx={{ width: '100%' }}>
                <ListItemButton onClick={() => handleSectionToggle(section.id)} sx={sectionHeaderStyle}>
                  <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 1.5 : 'auto', justifyContent: 'center' }}>
                    <SectionIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={section.title}
                    primaryTypographyProps={{
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      color: '#3A3A3A',
                      noWrap: true,
                    }}
                    sx={{
                      opacity: isExpanded ? 1 : 0,
                      display: isExpanded ? 'block' : 'none',
                      minWidth: 0,
                      flex: 1,
                    }}
                  />
                  {isExpanded && (open ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />)}
                </ListItemButton>
              </ListItem>
              <Collapse in={open && isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {section.items.map((item) => renderMenuItem(item))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
